import { useState, useEffect } from "react";
import { Box, Tabs, Text, Group } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import { IconRobot } from "@tabler/icons-react";
import type {
    AgentExecutionViewModel,
    FlowConfigViewModel,
    FlowStepViewModel,
    LLMConfigOptionsViewModel,
    AgentRuntimeOptionsValue,
} from "@models/doctype";
import { useModelCatalog } from "@hooks/flow/useModelCatalog";
import { useFlowConfig } from "@hooks/flow/useFlowConfig";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import { useTranslation } from "@hooks/useTranslation";
import { parseJsonText } from "@utils/jsonText";
import { ProviderModelSelect } from "../ProviderModelSelect";
import { ConfigOptionsFields } from "../ConfigOptionsFields";
import { RuntimeConfigOptionsFields } from "../RuntimeConfigOptionsFields";
import { AgentRuntimeOptionsFields } from "../AgentRuntimeOptionsFields";
import { CodeEditor } from "../CodeEditor";
import { EditorDrawer, TabLabel } from "../EditorDrawer";
import { buildAgentUpdateRequest, buildConfigOptionsFromForm } from "@utils/flowConfigHelpers";

interface AgentDetailModalProps {
    opened: boolean;
    onClose: () => void;
    agent: AgentExecutionViewModel;
    step: FlowStepViewModel;
    flowConfig: FlowConfigViewModel;
    docTypeId: string;
    /** Called with the new version the save created. */
    onSaved?: (config: FlowConfigViewModel) => void;
    /** The version number a save will create; defaults to the next one after `flowConfig`. */
    nextVersion?: number;
    canEdit?: boolean;
}

export const AgentDetailModal = ({
    opened,
    onClose,
    agent,
    step,
    flowConfig,
    docTypeId,
    onSaved,
    nextVersion = flowConfig.version + 1,
    canEdit = true,
}: AgentDetailModalProps) => {
    const { t } = useTranslation();
    const { canCreateDoctype } = useAdminPermissions();
    const { createFlowConfig, isLoading, error: saveError, clearError } = useFlowConfig();
    const catalog = useModelCatalog("chat", opened && canCreateDoctype);

    const [activeTab, setActiveTab] = useState("0");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [userPrompt, setUserPrompt] = useState("");
    const [responseSchema, setResponseSchema] = useState("");
    const [modelName, setModelName] = useState("");
    const [temperature, setTemperature] = useState<number | string>("");
    const [topP, setTopP] = useState<number | string>("");
    const [topK, setTopK] = useState<number | string>("");
    const [maxOutputTokens, setMaxOutputTokens] = useState<number | string>("");
    const [thinkingBudget, setThinkingBudget] = useState<number | string>("");
    const [seed, setSeed] = useState<number | string>("");
    const [runtimeConfigOptions, setRuntimeConfigOptions] =
        useState<LLMConfigOptionsViewModel | null>(null);
    const [agentRuntimeOptions, setAgentRuntimeOptions] =
        useState<AgentRuntimeOptionsValue | null>(null);
    const [hasChanges, setHasChanges] = useState(false);

    // The agent's own model reference (provider + model); an empty model means
    // the agent runs on the step's model, whose provider is preselected.
    const [selectedProvider, setSelectedProvider] = useState("");

    useEffect(() => {
        if (!opened) return;
        const provider = agent.provider || step.llmStepConfig?.provider || "";
        setSelectedProvider(provider);
        if (provider && canCreateDoctype) void catalog.loadModels(provider);
        setActiveTab("0");
        setSystemPrompt(agent.systemPrompt ?? "");
        setUserPrompt(agent.userPrompt ?? "");
        setResponseSchema(agent.responseSchema ? JSON.stringify(agent.responseSchema, null, 2) : "");
        setModelName(agent.modelName ?? "");
        setTemperature(agent.configOptions?.temperature ?? "");
        setTopP(agent.configOptions?.topP ?? "");
        setTopK(agent.configOptions?.topK ?? "");
        setMaxOutputTokens(agent.configOptions?.maxOutputTokens ?? "");
        setThinkingBudget(agent.configOptions?.thinkingBudget ?? "");
        setSeed(agent.configOptions?.seed ?? "");
        setRuntimeConfigOptions(agent.configOptions ? { ...agent.configOptions } : null);
        setAgentRuntimeOptions(agent.fallbackModel ? { fallbackModel: agent.fallbackModel } : null);
        setHasChanges(false);
        clearError();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agent, step, opened]);

    const markChanged = () => { if (!hasChanges) setHasChanges(true); };
    const withChange = <T,>(setter: (value: T) => void) => (value: T) => {
        setter(value);
        markChanged();
    };

    const schema = parseJsonText(responseSchema);
    const userPromptMissing = !userPrompt.trim();
    // Every agent needs its own schema; the step's is never used for agents.
    const schemaMissing = schema.ok && !schema.value;
    const isValid = schema.ok && !schemaMissing && !userPromptMissing;

    const handleProviderChange = (providerKey: string) => {
        setSelectedProvider(providerKey);
        setModelName("");
        markChanged();
        void catalog.loadModels(providerKey);
    };

    const handleSave = async () => {
        if (!isValid || !schema.ok || !schema.value) return;
        const request = buildAgentUpdateRequest(flowConfig, step, agent, {
            domainKey: agent.domainKey,
            userPrompt,
            responseSchema: schema.value,
            responseSchemaText: responseSchema,
            systemPrompt,
            provider: selectedProvider,
            modelPrefix: agent.modelPrefix,
            modelName,
            configOptions: buildConfigOptionsFromForm(
                { temperature, topP, topK, maxOutputTokens, thinkingBudget, seed },
                runtimeConfigOptions,
            ),
            fallbackModel: agentRuntimeOptions?.fallbackModel,
        });

        const result = await createFlowConfig(docTypeId, request);
        if (result) {
            setHasChanges(false);
            onSaved?.(result);
            onClose();
        }
    };

    const parentConfig = step.llmStepConfig!;

    return (
        <EditorDrawer
            opened={opened}
            onClose={onClose}
            title={
                <Box>
                    <Group gap="sm">
                        <IconRobot size={22} color="var(--mantine-color-violet-6)" />
                        <Text fw={700} size="lg">{agent.domainKey}</Text>
                    </Group>
                    <Text size="xs" c="dimmed">
                        {t("admin.flowConfigs.editor.editingVersion", { version: flowConfig.version, next: nextVersion })}
                    </Text>
                </Box>
            }
            dirty={hasChanges}
            saving={isLoading}
            error={saveError}
            onDismissError={clearError}
            saveLabel={t("admin.flowConfigs.editor.saveAsVersion", { version: nextVersion })}
            blockedReason={hasChanges && !isValid ? t("admin.flowConfigs.editor.fixErrors") : null}
            onSave={handleSave}
            canEdit={canEdit}
        >
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "0")} keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="0"><TabLabel label={t("admin.flowConfigs.labels.llmConfig")} /></Tabs.Tab>
                    <Tabs.Tab value="1"><TabLabel label={t("admin.flowConfigs.labels.systemPrompt")} /></Tabs.Tab>
                    <Tabs.Tab value="2">
                        <TabLabel label={t("admin.flowConfigs.labels.userPrompt")} invalid={userPromptMissing} />
                    </Tabs.Tab>
                    <Tabs.Tab value="3">
                        <TabLabel label={t("admin.flowConfigs.labels.responseSchema")} invalid={!schema.ok || schemaMissing} />
                    </Tabs.Tab>
                    <Tabs.Tab value="4"><TabLabel label={t("admin.flowConfigs.labels.runtimeOptions")} /></Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="0" className="editor-drawer__panel">
                    <Box mb="lg">
                        <ProviderModelSelect
                            isLoadingProviders={catalog.isLoadingProviders}
                            isLoadingModels={catalog.isLoadingModels}
                            providers={catalog.providers}
                            models={catalog.modelsFor(selectedProvider)}
                            selectedProvider={selectedProvider}
                            modelName={modelName}
                            modelPlaceholder={step.llmStepConfig?.modelName}
                            onProviderChange={handleProviderChange}
                            onModelChange={withChange(setModelName)}
                        />
                    </Box>
                    <Text size="sm" fw={600} mb="xs">{t("admin.flowConfigs.labels.configOptions")}</Text>
                    <ConfigOptionsFields
                        temperature={temperature}
                        topP={topP}
                        topK={topK}
                        maxOutputTokens={maxOutputTokens}
                        thinkingBudget={thinkingBudget}
                        seed={seed}
                        onTemperatureChange={withChange(setTemperature)}
                        onTopPChange={withChange(setTopP)}
                        onTopKChange={withChange(setTopK)}
                        onMaxOutputTokensChange={withChange(setMaxOutputTokens)}
                        onThinkingBudgetChange={withChange(setThinkingBudget)}
                        onSeedChange={withChange(setSeed)}
                    />
                    <RuntimeConfigOptionsFields
                        stepType="DATA_EXTRACTION"
                        value={runtimeConfigOptions}
                        onChange={setRuntimeConfigOptions}
                        onDirty={markChanged}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="1" className="editor-drawer__panel">
                    <Group gap="xs" mb="sm">
                        <Badge size="sm" variant="light" color={systemPrompt ? "violet" : "gray"}>
                            {systemPrompt
                                ? t("admin.flowConfigs.agents.agentOverride")
                                : t("admin.flowConfigs.agents.usingStepDefault")}
                        </Badge>
                    </Group>
                    <CodeEditor
                        language="text"
                        label={t("admin.flowConfigs.agents.systemPromptOverride")}
                        description={t("admin.flowConfigs.agents.systemPromptOverrideDesc")}
                        value={systemPrompt}
                        onChange={withChange(setSystemPrompt)}
                        placeholder={parentConfig.systemPrompt}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="2" className="editor-drawer__panel">
                    <CodeEditor
                        language="text"
                        label={t("admin.flowConfigs.labels.userPrompt")}
                        value={userPrompt}
                        onChange={withChange(setUserPrompt)}
                        required
                        error={userPromptMissing ? t("admin.flowConfigs.editor.required") : null}
                    />
                </Tabs.Panel>

                <Tabs.Panel value="3" className="editor-drawer__panel">
                    <CodeEditor
                        language="json"
                        label={t("admin.flowConfigs.labels.responseSchema")}
                        description={t("admin.flowConfigs.editor.schemaRequiredForAgent")}
                        required
                        error={schemaMissing ? t("admin.flowConfigs.editor.required") : null}
                        value={responseSchema}
                        onChange={withChange(setResponseSchema)}
                        placeholder='{"type": "object", "properties": {...}}'
                    />
                </Tabs.Panel>

                <Tabs.Panel value="4" className="editor-drawer__panel">
                    <AgentRuntimeOptionsFields
                        value={agentRuntimeOptions}
                        onChange={setAgentRuntimeOptions}
                        onDirty={markChanged}
                    />
                </Tabs.Panel>
            </Tabs>
        </EditorDrawer>
    );
};
